import React from 'react';
import { useQuery } from '@tanstack/react-query';
import MapIconsService from '../../../../../services/MapIconService/mapIcon.service';
import { findBuiltInShape, isBuiltInShape } from './builtInShapes';
import { SingleSymbologyConfig } from '../../../../../types/symbology.types';

/**
 * The small Symbol-cell preview for a unique category / quantity class:
 * the uploaded icon thumbnail (resolved to a presigned URL) for image icons,
 * the built-in shape filled with the configured fill colour, or a fill/outline
 * swatch for non-point geometries.
 */
const SymbologySwatch: React.FC<{ symbology: SingleSymbologyConfig; isPointLayer: boolean; isLineLayer?: boolean }> = ({
  symbology,
  isPointLayer,
  isLineLayer = false,
}) => {
  const { fetchIconUrl } = MapIconsService();
  const iconKey = symbology.icon?.key;
  const isImageIcon = !!(isPointLayer && iconKey && !isBuiltInShape(iconKey));
  const { data: iconUrl } = useQuery(fetchIconUrl(isImageIcon ? iconKey : undefined));

  if (isImageIcon) {
    return iconUrl ? (
      <img src={iconUrl} className="lp-unique__swatch-img" alt="" />
    ) : (
      <span className="lp-unique__swatch lp-unique__swatch--img" title="Image icon" />
    );
  }
  const shape = isPointLayer && symbology.icon ? findBuiltInShape(symbology.icon.key) : undefined;
  if (shape) {
    return (
      <svg viewBox="0 0 24 24" className="lp-unique__swatch-svg" style={{ fill: symbology.fill }} aria-hidden>
        <path d={shape.path} />
      </svg>
    );
  }
  // Line geometries show a stroke-coloured line rather than a fill swatch.
  if (isLineLayer) {
    return <span className="lp-unique__swatch-line" style={{ backgroundColor: symbology.outline || symbology.fill }} />;
  }
  // Solid fill dot (no contrasting outline ring — the thin neutral border in
  // CSS is only for visibility on light fills).
  return <span className="lp-unique__swatch" style={{ backgroundColor: symbology.fill }} />;
};

export default SymbologySwatch;
